import {ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {DataService} from '../../../../Services/data.service';
import {StorageService} from '../../../../Services/storage.service';
import {Title} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {ReportPrintService} from '../../../../Services/report-print.service';
import {SendEmailModalComponent} from '../../../common/send-email-modal/send-email-modal.component';
import {EmailModel} from '../../../../DataModels/email-model';
import {Modal} from 'bootstrap';
import {DateFilterService} from '../../../../Services/dateFilter.service';

export interface CrmPipelineRow {
  opportunity_id: string;
  title: string;
  entity_name: string;
  contact_name: string;
  salesperson_id: string;
  opportunity_type: string;
  source: string;
  stage_id: string;
  stage_name: string;
  stage_sort_order: number;
  probability: number;
  estimated_value: number;
  expected_close_date: string;
  status: string;
  created_date: string;
}

export interface CrmPipelineStageGroup {
  stage_name: string;
  stage_sort_order: number;
  rows: CrmPipelineRow[];
  totalValue: number;
}

@Component({
  selector: 'app-crm-pipeline-report',
  templateUrl: './crm-pipeline-report.component.html',
  styleUrls: [],
  standalone: false,
})
export class CrmPipelineReportComponent implements OnInit {

  @ViewChild('ReportOutput', {static: false}) ReportOutput: ElementRef;
  @ViewChild(SendEmailModalComponent, {read: ElementRef}) private localSendEmailModalHost: ElementRef;

  rptName = 'Opportunity Pipeline Summary';
  isLoading = false;
  noDataFound = false;
  rows: CrmPipelineRow[] = [];
  groups: CrmPipelineStageGroup[] = [];
  grandTotalCount = 0;
  grandTotalValue = 0;
  users: any[] = [];
  reportDataToSendInEmail: EmailModel = new EmailModel();

  buttonConfig: any = {
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: false,
    close: true,
    clear: false,
    filters: false,
    reset: true,
  };

  filters: any = {
    company_id: '',
    salesperson_id: '',
    opportunity_type: '',
    status: '',
    from_date: '',
    to_date: '',
  };

  opportunityTypeOptions = [
    {value: '', label: '— All —'},
    {value: 'new_business', label: 'New Business'},
    {value: 'upsell', label: 'Upsell'},
    {value: 'renewal', label: 'Renewal'},
    {value: 'vendor_negotiation', label: 'Vendor Negotiation'},
  ];

  statusOptions = [
    {value: '', label: '— All —'},
    {value: 'open', label: 'Open'},
    {value: 'won', label: 'Won'},
    {value: 'lost', label: 'Lost'},
    {value: 'abandoned', label: 'Abandoned'},
  ];

  vm: any = {Preferences: []};

  constructor(
    private titleService: Title,
    private router: Router,
    private tableDataService: DataService,
    private localStorage: StorageService,
    private notification: BsToastService,
    private reportPrint: ReportPrintService,
    private cd: ChangeDetectorRef,
    private dateFilter: DateFilterService,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle(this.rptName);
    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
    this.filters.company_id = this.vm.Preferences?.defaults?.company_id ?? '';

    this.tableDataService.getApiListData('sys_user', '?pageNumber=0&pageSize=999').subscribe({
      next: (result: any) => {
        this.users = result.Data ?? result.items ?? [];
      },
    });
  }

  getUserName(userId: string): string {
    if (!userId) {
      return '—';
    }
    const u = this.users.find(x => x.id === userId);
    return u ? u.name : userId;
  }

  resetFilters() {
    this.filters = {
      company_id: this.vm.Preferences?.defaults?.company_id ?? '',
      salesperson_id: '',
      opportunity_type: '',
      status: '',
      from_date: '',
      to_date: '',
    };
    this.rows = [];
    this.groups = [];
    this.vm.report = null;
  }

  close() {
    this.router.navigate(['/home']);
  }

  runReport() {
    this.isLoading = true;
    this.noDataFound = false;
    this.rows = [];
    this.groups = [];

    let params = 'company_id=' + (this.filters.company_id || '');
    if (this.filters.salesperson_id) {
      params += '&salesperson_id=' + this.filters.salesperson_id;
    }
    if (this.filters.opportunity_type) {
      params += '&opportunity_type=' + this.filters.opportunity_type;
    }
    if (this.filters.status) {
      params += '&status=' + this.filters.status;
    }
    const fd = this.dateFilter.GetDateFormat(this.filters.from_date);
    const td = this.dateFilter.GetDateFormat(this.filters.to_date);
    if (fd) {
      params += '&from_date=' + fd;
    }
    if (td) {
      params += '&to_date=' + td;
    }

    this.tableDataService.getDataWithParams('CrmPipelineReport', params).subscribe({
      next: (result: any) => {
        this.rows = Array.isArray(result) ? result : (result.Data ?? result.items ?? []);
        this.buildGroups();
        this.noDataFound = this.rows.length === 0;
        this.vm.report = this.rows;
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.notification.showErrorToast('Error loading report.');
        this.isLoading = false;
      },
    });
  }

  private buildGroups() {
    const map = new Map<string, CrmPipelineStageGroup>();
    for (const r of this.rows) {
      if (!map.has(r.stage_id)) {
        map.set(r.stage_id, {stage_name: r.stage_name, stage_sort_order: r.stage_sort_order, rows: [], totalValue: 0});
      }
      const g = map.get(r.stage_id)!;
      g.rows.push(r);
      g.totalValue += r.estimated_value ?? 0;
    }
    this.groups = Array.from(map.values()).sort((a, b) => a.stage_sort_order - b.stage_sort_order);
    this.grandTotalCount = this.rows.length;
    this.grandTotalValue = this.rows.reduce((s, r) => s + (r.estimated_value ?? 0), 0);
  }

  printReport() {
    try {
      const html = this.buildPrintHtml();
      this.reportPrint.printInIframe(html);
    } catch (e) {
      console.error('CRM Pipeline Report print failed', e);
    }
  }

  setEmailData() {
    const emailDocObj = {} as EmailModel;
    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '';
    emailDocObj.toEmail = '';
    emailDocObj.body = 'Please see the attached ' + this.rptName + '.<br><br>Best regards,<br><br>AcctXErp<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + this.rptName;
    try {
      this.cd.detectChanges();
    } catch {
    }
    try {
      emailDocObj.pdfHtml = this.buildPrintHtml(true);
    } catch {
      emailDocObj.pdfHtml = this.ReportOutput?.nativeElement?.outerHTML || '';
    }
    emailDocObj.activity_message = this.rptName;
    const now = new Date();
    emailDocObj.FileName = `CrmPipelineReport_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.pdf`;
    this.reportDataToSendInEmail = emailDocObj;
    const element = document.getElementById('sendEmailModal') as HTMLElement;
    if (element) {
      const emailModal = new Modal(element);
      emailModal.show();
    }
  }

  private buildPrintHtml(forEmail: boolean = false): string {
    const rawContent = (this.ReportOutput?.nativeElement?.outerHTML || '').toString();
    const content = this.reportPrint.cleanContentHtml(rawContent);
    const meta: { label: string; value: string }[] = [];
    return this.reportPrint.buildHtml({title: this.rptName, contentHtml: content, meta, orientation: 'landscape'});
  }
}
